import { FC, KeyboardEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useFuelEmissionFactors,
  useModalityEmissionFactors,
} from "./emissionFactorsData";
import { isActiveOn, matchesSearch } from "./emissionFactorsFilters";
import "./EmissionFactors.scss";

type Tab = "fuel" | "modality";

const EmissionFactors: FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("fuel");
  const [activeOnly, setActiveOnly] = useState(false);
  const [search, setSearch] = useState("");

  const fuel = useFuelEmissionFactors();
  const modality = useModalityEmissionFactors();
  const today = new Date();

  const fuelRows = (fuel.data ?? [])
    .filter((r) => (activeOnly ? isActiveOn(r.periodStartDate, r.periodEndDate, today) : true))
    .filter((r) => matchesSearch([r.fuelType], search));

  const modalityRows = (modality.data ?? [])
    .filter((r) => (activeOnly ? isActiveOn(r.periodStartDate, r.periodEndDate, today) : true))
    .filter((r) => matchesSearch([r.transport, r.modality, r.modalityDetail], search));

  const fuelTabRef = useRef<HTMLButtonElement>(null);
  const modalityTabRef = useRef<HTMLButtonElement>(null);

  // Move both selection and focus to a tab (automatic-activation ARIA pattern).
  const selectTab = (next: Tab) => {
    setTab(next);
    (next === "fuel" ? fuelTabRef : modalityTabRef).current?.focus();
  };

  const onTabsKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowLeft":
        e.preventDefault();
        selectTab(tab === "fuel" ? "modality" : "fuel");
        break;
      case "Home":
        e.preventDefault();
        selectTab("fuel");
        break;
      case "End":
        e.preventDefault();
        selectTab("modality");
        break;
      default:
        break;
    }
  };

  const searchPlaceholder =
    tab === "fuel"
      ? t("emissionFactors.searchPlaceholderFuel")
      : t("emissionFactors.searchPlaceholderModality");

  return (
    <div className="emission-factors container">
      <h1 className="emission-factors__title">{t("emissionFactors.title")}</h1>
      <p className="emission-factors__intro">{t("emissionFactors.intro")}</p>

      <div className="emission-factors__tabs" role="tablist">
        <button
          ref={fuelTabRef}
          type="button"
          role="tab"
          id="emission-factors-tab-fuel"
          aria-selected={tab === "fuel"}
          aria-controls="emission-factors-panel-fuel"
          tabIndex={tab === "fuel" ? 0 : -1}
          className={tab === "fuel" ? "is-active" : ""}
          onClick={() => setTab("fuel")}
          onKeyDown={onTabsKeyDown}
        >
          {t("emissionFactors.tabFuel")}
        </button>
        <button
          ref={modalityTabRef}
          type="button"
          role="tab"
          id="emission-factors-tab-modality"
          aria-selected={tab === "modality"}
          aria-controls="emission-factors-panel-modality"
          tabIndex={tab === "modality" ? 0 : -1}
          className={tab === "modality" ? "is-active" : ""}
          onClick={() => setTab("modality")}
          onKeyDown={onTabsKeyDown}
        >
          {t("emissionFactors.tabModality")}
        </button>
      </div>

      <div className="emission-factors__filters">
        <input
          type="search"
          className="emission-factors__search"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label htmlFor="emission-factors-active-only" className="emission-factors__toggle">
          <input
            id="emission-factors-active-only"
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          {t("emissionFactors.activeOnly")}
        </label>
      </div>

      <div
        className="emission-factors__panel"
        role="tabpanel"
        id="emission-factors-panel-fuel"
        aria-labelledby="emission-factors-tab-fuel"
        hidden={tab !== "fuel"}
        tabIndex={0}
      >
        <div className="emission-factors__card">
          {fuel.isLoading && (
            <div className="emission-factors__state">
              {t("common.loading", "Loading…")}
            </div>
          )}
          {fuel.isError && (
            <div className="emission-factors__state">
              {t("common.error", "Something went wrong. Please try again later.")}
            </div>
          )}
          {!fuel.isLoading && !fuel.isError && (
            <div className="emission-factors__wrapper">
              <table className="emission-factors__table">
                <thead>
                  <tr>
                    <th>{t("emissionFactors.colFuelType")}</th>
                    <th>WTW</th>
                    <th>TTW</th>
                    <th>WTT</th>
                    <th>{t("emissionFactors.colUnit")}</th>
                    <th>{t("emissionFactors.colValidFrom")}</th>
                    <th>{t("emissionFactors.colValidTo")}</th>
                    <th>{t("emissionFactors.colSource")}</th>
                  </tr>
                </thead>
                <tbody>
                  {fuelRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.fuelType}</td>
                      <td>{r.wtwFactor}</td>
                      <td>{r.ttwFactor}</td>
                      <td>{r.wttFactor}</td>
                      <td>{r.unit}</td>
                      <td>{r.periodStartDate ?? "—"}</td>
                      <td>{r.periodEndDate ?? "—"}</td>
                      <td>{r.source ?? "—"}</td>
                    </tr>
                  ))}
                  {fuelRows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="emission-factors__empty">
                        {t("emissionFactors.empty")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div
        className="emission-factors__panel"
        role="tabpanel"
        id="emission-factors-panel-modality"
        aria-labelledby="emission-factors-tab-modality"
        hidden={tab !== "modality"}
        tabIndex={0}
      >
        <div className="emission-factors__card">
          {modality.isLoading && (
            <div className="emission-factors__state">
              {t("common.loading", "Loading…")}
            </div>
          )}
          {modality.isError && (
            <div className="emission-factors__state">
              {t("common.error", "Something went wrong. Please try again later.")}
            </div>
          )}
          {!modality.isLoading && !modality.isError && (
            <div className="emission-factors__wrapper">
              <table className="emission-factors__table">
                <thead>
                  <tr>
                    <th>{t("emissionFactors.colTransport")}</th>
                    <th>{t("emissionFactors.colModality")}</th>
                    <th>{t("emissionFactors.colDetail")}</th>
                    <th>WTW</th>
                    <th>TTW</th>
                    <th>WTT</th>
                    <th>{t("emissionFactors.colValidFrom")}</th>
                    <th>{t("emissionFactors.colValidTo")}</th>
                    <th>{t("emissionFactors.colSource")}</th>
                  </tr>
                </thead>
                <tbody>
                  {modalityRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.transport}</td>
                      <td>{r.modality}</td>
                      <td>{r.modalityDetail}</td>
                      <td>{r.wtwFactor}</td>
                      <td>{r.ttwFactor}</td>
                      <td>{r.wttFactor}</td>
                      <td>{r.periodStartDate ?? "—"}</td>
                      <td>{r.periodEndDate ?? "—"}</td>
                      <td>{r.source ?? "—"}</td>
                    </tr>
                  ))}
                  {modalityRows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="emission-factors__empty">
                        {t("emissionFactors.empty")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmissionFactors;
